/**
 * What each gate says when it refuses.
 *
 * Ported from the legacy app's MarketingModal set
 * (`edge-frontend/src/app/components/MarketingModal/`), which kept one
 * component per pitch. The wording for dilution, exports, expressions,
 * layouts, panels and history is close to the legacy copy because it was
 * written for these exact features; the rest is new, in the same voice.
 *
 * Two shapes, which the legacy app also had:
 *   locked  the plan does not include it, so lead with the value
 *   limit   the plan includes it and the allowance is spent, so lead with the
 *           fact, naming the plan and the number the user just hit
 *
 * Copy lives here and nowhere else. A call site names the gate; it never
 * writes a sentence, never names a plan, and never quotes a number.
 */
import {
  BarChart3,
  Boxes,
  Braces,
  CalendarRange,
  Columns3,
  Download,
  FileText,
  Filter,
  LayoutGrid,
  LineChart,
  ListChecks,
  Sparkles,
  SwatchBook,
  Telescope,
  TrendingUp,
  Waypoints,
  Wrench,
  type LucideIcon,
} from 'lucide-react'
import type { Feature } from '@/context/plan-constraint-utils'

export interface UpgradeCopyContext {
  /** The plan the user is on now, as sold, e.g. "Basic". */
  currentPlanLabel: string
  /** The plan being pitched, as sold, e.g. "Pro". */
  targetPlanLabel: string
  /** The allowance just hit, for a `limit` pitch. */
  limit?: number
  /** What the allowance counts, singular, e.g. "Scanner". */
  label?: string
}

export interface UpgradeEntry {
  icon: LucideIcon
  /** Opening paragraph when the feature is not on the plan at all. */
  locked: (c: UpgradeCopyContext) => string
  /** Opening paragraph when the allowance is spent. Falls back to `locked`. */
  limit?: (c: UpgradeCopyContext) => string
  calloutTitle: (c: UpgradeCopyContext) => string
  calloutBody: (c: UpgradeCopyContext) => string
}

/** "2 Scanner Panels", "1 custom layout": the phrase a limit pitch needs. */
function countOf(c: UpgradeCopyContext, plural: string, singular: string): string {
  const n = c.limit ?? 0
  const noun = c.label ? `${c.label} ${n === 1 ? singular : plural}` : n === 1 ? singular : plural
  return `${n} ${noun}`
}

export const UPGRADE_FALLBACK: UpgradeEntry = {
  icon: Sparkles,
  locked: (c) =>
    `This is part of ${c.targetPlanLabel}. Your ${c.currentPlanLabel} plan does not include it yet.`,
  calloutTitle: (c) => `Everything in ${c.targetPlanLabel}`,
  calloutBody: () => 'Move up a plan to unlock this and the rest of the tools that come with it',
}

export const UPGRADE_CONTENT: Record<Feature, UpgradeEntry> = {
  // ── Ported from the legacy modals ────────────────────────────
  'keystats-dilution': {
    icon: TrendingUp,
    locked: () =>
      'Unlock detailed dilution analysis to understand how share structures impact company valuations and shareholder equity.',
    calloutTitle: () => 'Advanced dilution metrics',
    calloutBody: () =>
      'Track share dilution trends, analyze ownership changes, and monitor equity dilution over time with comprehensive dilution data',
  },
  exports: {
    icon: Download,
    locked: (c) => `Your current ${c.currentPlanLabel} plan can only preview this feature.`,
    calloutTitle: () => 'Download your data directly',
    calloutBody: () =>
      'Export the rows behind any query as CSV, and create as many export profiles as you need',
  },
  expressions: {
    icon: Braces,
    locked: () => 'Define your own metrics and use them anywhere a column, filter or stat can go.',
    limit: (c) =>
      `Your current ${c.currentPlanLabel} plan is limited to ${countOf(c, 'expressions', 'expression')}.`,
    calloutTitle: () => 'Build your own metrics',
    calloutBody: () =>
      'Define your own calculations to be used inside filter, column, and stats profiles',
  },
  'history-custom-layouts': {
    icon: LayoutGrid,
    locked: (c) =>
      `Your current ${c.currentPlanLabel} plan does not include custom layout creation and editing.`,
    limit: (c) =>
      `Your current ${c.currentPlanLabel} plan is limited to ${countOf(c, 'custom layouts', 'custom layout')}.`,
    calloutTitle: () => 'Fully customizable layouts',
    calloutBody: () =>
      'Arrange panels exactly how you work, with complete control over placement, sizing and configuration',
  },
  'history-unlimited-layouts': {
    icon: LayoutGrid,
    locked: () => 'Save as many layouts as you want, with no ceiling to work around.',
    limit: (c) =>
      `Your current ${c.currentPlanLabel} plan is limited to ${countOf(c, 'custom layouts', 'custom layout')}.`,
    calloutTitle: () => 'Unlimited layouts',
    calloutBody: () =>
      'Keep a layout per setup, per ticker or per time of day, and stop deleting one to make room for another',
  },
  scanner: {
    icon: Columns3,
    locked: () => 'The scanner is the core of the platform.',
    limit: (c) =>
      `Your current ${c.currentPlanLabel} plan is limited to ${countOf(c, 'Panels', 'Panel')}.`,
    calloutTitle: () => 'More scanners at once',
    calloutBody: () =>
      'Run several scanners side by side, each with its own filters, instead of switching one back and forth',
  },
  'history-market-stats': {
    icon: BarChart3,
    locked: () =>
      'See how a setup has actually performed, aggregated across every matching day in history.',
    calloutTitle: () => 'Market Stats',
    calloutBody: () =>
      'Averages, counts and distributions over your whole result set, so a pattern is a number rather than a hunch',
  },
  'ticker-stats-custom': {
    icon: ListChecks,
    locked: (c) =>
      `Your current ${c.currentPlanLabel} plan can use the built-in Ticker Stats presets, but not build your own.`,
    calloutTitle: () => 'Customizable Ticker Stats',
    calloutBody: () =>
      'Build your own stats profiles: averages, sums, standard deviations and more, over the fields you care about',
  },
  // ── New gates the legacy app never had ───────────────────────
  backtest: {
    icon: LineChart,
    locked: () => 'Test an idea against years of history before you risk anything on it.',
    limit: (c) =>
      `Your current ${c.currentPlanLabel} plan is limited to ${countOf(c, 'runs', 'run')} a month.`,
    calloutTitle: () => 'The Backtester',
    calloutBody: () =>
      'Build a strategy as a graph, run it over historical days, and read the results trade by trade',
  },
  'ai-analyze': {
    icon: Sparkles,
    locked: () =>
      'Ask market questions in plain language and get answers built from the data, not from a search box.',
    calloutTitle: () => 'AI Analyze',
    calloutBody: () =>
      'Point the assistant at a ticker, a date or a screen and it answers with numbers first, then the reasoning',
  },
  'ai-build': {
    icon: Wrench,
    locked: () => 'Let the assistant do the work: build scanners, strategies and layouts for you.',
    calloutTitle: () => 'AI actions',
    calloutBody: () =>
      'Describe what you want and it creates and configures it, instead of telling you where to click',
  },
  'deep-research': {
    icon: Telescope,
    locked: () =>
      'Study a setup across years of trading days and get a full report with the charts and the numbers behind it.',
    limit: (c) =>
      `Your current ${c.currentPlanLabel} plan includes ${countOf(c, 'reports', 'report')} a month, and they are spent.`,
    calloutTitle: () => 'Deep Research reports',
    calloutBody: () =>
      'Each report counts a setup over many past trading days, with charts, tables and the caveats in one place',
  },
  'theme-tracker': {
    icon: SwatchBook,
    locked: () =>
      'Follow what the market is actually rotating into, as a theme rather than a ticker.',
    calloutTitle: () => 'Theme Tracker',
    calloutBody: () =>
      'Watch themes build and fade across sectors, and jump straight from a theme to the names inside it',
  },
  notebook: {
    icon: FileText,
    locked: () => 'Keep your process, your notes and your trades in one place.',
    calloutTitle: () => 'Notebook',
    calloutBody: () =>
      'Write up a thesis, track how it played out, and link it to the tickers and dates it came from',
  },
  'history-sankey': {
    icon: Waypoints,
    locked: () => 'See where the days that match your filters actually end up, stage by stage.',
    calloutTitle: () => 'Flow Graph',
    calloutBody: () =>
      'Follow a set of days through each condition and watch where the population splits and drains away',
  },
  'multi-scanner': {
    icon: Boxes,
    locked: () => 'Run more than one scanner in a single panel.',
    calloutTitle: () => 'Multi-Scanner',
    calloutBody: () =>
      'Stack several scans in one panel and switch between them without rebuilding a filter',
  },
  'multi-chart': {
    icon: LineChart,
    locked: () => 'Chart every name in a scan at once, not one at a time.',
    calloutTitle: () => 'Chart Scanner',
    calloutBody: () =>
      'A grid of charts driven by a scanner, so you read the whole list visually in one pass',
  },
  'advanced-filters': {
    icon: Filter,
    locked: () => 'Ask harder questions of the data.',
    calloutTitle: () => 'Advanced filters',
    calloutBody: () =>
      'Nest conditions, compare columns to each other, and filter on the values you defined yourself',
  },
  history: {
    icon: CalendarRange,
    locked: () => 'Study what has already happened, not just what is happening now.',
    calloutTitle: () => 'The History page',
    calloutBody: () =>
      'Query years of daily data, then break the results down however the question needs',
  },
  watchlist: {
    icon: ListChecks,
    locked: () => 'Keep the names you care about in front of you.',
    calloutTitle: () => 'Watchlists',
    calloutBody: () => 'Track your own lists with the same columns and alerts as the scanner',
  },
}
